import { useEffect, useState } from "react";
import { CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, formatPhone, validPhone, validEmail } from "@/lib/api";
import { getAttribution } from "@/lib/tracking";
import { US_STATES } from "@/lib/constants";
import { Field, inputCls, textareaCls, Btn } from "@/components/common";

export const SECTIONS = {
  booking: [["contact", "Your contact details"], ["address", "Trial address"], ["trial", "Trial preferences"], ["lawn", "About your lawn"], ["extra", "Additional information"]],
  contact: [["contact", "Contact details"], ["extra", "More information"]],
};
export const FIELD_TYPES = [["text", "Short text"], ["textarea", "Long text"], ["email", "Email"], ["tel", "US phone"], ["zip", "ZIP code"], ["date", "Date"], ["number", "Number"], ["select", "Dropdown"], ["checkbox", "Checkbox"]];

export function optionsFor(f, ctx = {}) {
  if (f.dynamic === "states") return US_STATES.map((s) => [s, s]);
  if (f.dynamic === "products") return (ctx.products || []).map((p) => [p.id, p.name]);
  if (f.dynamic === "delivery") return (ctx.delivery || []).map((d) => [d, d === "delivery" ? "Delivery to my address" : d === "pickup" ? "I'll pick it up" : d]);
  return f.options || [];
}

export function validateFields(fields, vals) {
  const e = {};
  const today = new Date().toISOString().slice(0, 10);
  fields.forEach((f) => {
    const v = vals[f.key];
    if (f.type === "checkbox") { if (f.required && !v) e[f.key] = `${f.label} is required`; return; }
    const s = String(v ?? "").trim();
    if (!s) { if (f.required) e[f.key] = `${f.label} is required`; return; }
    if (f.type === "email" && !validEmail(s)) e[f.key] = "Enter a valid email address";
    if (f.type === "tel" && !validPhone(s)) e[f.key] = "Enter a valid 10-digit US phone number";
    if (f.type === "zip" && !/^\d{5}(-\d{4})?$/.test(s)) e[f.key] = "Enter a valid 5-digit ZIP code";
    if (f.key === "preferred_date" && s < today) e[f.key] = "Choose a future date";
  });
  return e;
}

export function splitValues(fields, vals) {
  const builtin = {}, custom = {};
  fields.forEach((f) => { (f.builtin ? builtin : custom)[f.key] = vals[f.key] ?? (f.type === "checkbox" ? false : ""); });
  return { ...builtin, custom };
}

export function FieldInput({ f, value, onChange, error, ctx, testPrefix }) {
  const tid = `${testPrefix}-${f.key.replace(/_/g, "-")}-input`;
  const common = { name: f.key, "data-testid": tid, placeholder: f.placeholder || undefined };
  const wide = f.type === "textarea" || f.key === "street";
  let input;
  if (f.type === "checkbox") {
    return (
      <label className="flex items-start gap-3 sm:col-span-2">
        <input type="checkbox" checked={!!value} onChange={(e) => onChange(e.target.checked)} {...common} className="mt-1 h-5 w-5 accent-emerald-700" />
        <span className="text-sm text-slate-700">{f.label}{f.required && <span className="text-emerald-700"> *</span>}{error && <span className="block text-xs font-medium text-rose-600">{error}</span>}</span>
      </label>
    );
  }
  if (f.type === "select") {
    input = <select value={value || ""} onChange={(e) => onChange(e.target.value)} {...common} className={inputCls}><option value="">{f.dynamic === "products" ? "Not sure yet" : "Select…"}</option>{optionsFor(f, ctx).map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select>;
  } else if (f.type === "textarea") {
    input = <textarea value={value || ""} onChange={(e) => onChange(e.target.value)} maxLength={2000} {...common} className={textareaCls} />;
  } else {
    const t = { tel: "tel", email: "email", date: "date", number: "number" }[f.type] || "text";
    const extra = f.key === "preferred_date" ? { min: new Date().toISOString().slice(0, 10), max: new Date(Date.now() + 180 * 864e5).toISOString().slice(0, 10) } : {};
    input = <input type={t} value={value || ""} onChange={(e) => onChange(f.type === "tel" ? formatPhone(e.target.value) : e.target.value)} inputMode={f.type === "zip" ? "numeric" : undefined} {...extra} {...common} className={inputCls} />;
  }
  return <Field label={f.label} required={f.required} error={error} className={wide ? "sm:col-span-2" : ""}>{input}</Field>;
}

export function useFormConfig(kind) {
  const [fields, setFields] = useState(null);
  useEffect(() => { api.get(`/public/forms/${kind}`).then((r) => setFields(r.data)).catch(() => setFields([])); }, [kind]);
  return fields;
}

export function ContactForm() {
  const fields = useFormConfig("contact");
  const [vals, setVals] = useState({});
  const [consent, setConsent] = useState(false);
  const [hp, setHp] = useState("");
  const [errors, setErrors] = useState({});
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);
  if (!fields) return null;
  const submit = async (e) => {
    e.preventDefault();
    const errs = validateFields(fields, vals);
    setErrors(errs);
    if (Object.keys(errs).length) return toast.error("Please fix the highlighted fields");
    if (!consent) return toast.error("Please accept the Privacy Policy");
    setLoading(true);
    try { await api.post("/public/contact", { ...splitValues(fields, vals), consent_terms: consent, company_website: hp, attribution: getAttribution() }); setDone(true); } catch (err) { toast.error(errMsg(err)); } finally { setLoading(false); }
  };
  if (done) return <div data-testid="contact-success" className="flex items-center gap-3 rounded-2xl border border-emerald-200 bg-emerald-50 p-6 text-emerald-900"><CheckCircle2 className="h-6 w-6" />Thanks — your message has been received. We'll get back to you soon.</div>;
  return (
    <form onSubmit={submit} noValidate className="grid gap-5 sm:grid-cols-2" data-testid="contact-form">
      <input name="company_website" value={hp} onChange={(e) => setHp(e.target.value)} tabIndex={-1} autoComplete="off" className="absolute -left-[9999px] opacity-0" aria-hidden="true" />
      {fields.map((f) => <FieldInput key={f.key} f={f} value={vals[f.key]} error={errors[f.key]} testPrefix="contact" onChange={(v) => { setVals((s) => ({ ...s, [f.key]: v })); setErrors((x) => ({ ...x, [f.key]: undefined })); }} />)}
      <label className="flex items-start gap-3 sm:col-span-2"><input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} data-testid="contact-consent-checkbox" className="mt-1 h-5 w-5 accent-emerald-700" /><span className="text-sm text-slate-700">I agree to the Privacy Policy and consent to being contacted about my enquiry.</span></label>
      <Btn type="submit" loading={loading} data-testid="contact-submit-button" className="sm:col-span-2 sm:w-fit">Send message</Btn>
    </form>
  );
}
